import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  InputNumber,
  Spin,
  Row,
  Col,
  Space,
  Tag,
  Tooltip,
  Modal,
  Switch,
} from "antd";
import API from "../../../../services/api";
import { uploadToCloudinary, uploadMultipleToCloudinary } from "../../../../utils/uploadToCloudinary";
import { useEffect, useState, useRef, useCallback } from "react";
import LocationField from "../../../LocationField";
import { useNavigate, useParams } from "react-router-dom";
import {
  EnvironmentOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./EditBusiness.css";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Match Add form options exactly
const INDUSTRY_OPTIONS = [
  "food",
  "retail",
  "education",
  "healthcare",
  "technology",
  "fitness",
  "beauty",
  "real_estate",
  "other",
];

const COMPANY_SIZE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "500+"];
const BUSINESS_TYPE_OPTIONS = ["startup", "private", "public", "agency", "partnership", "other"];

function EditBusiness() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [current, setCurrent] = useState(0);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [removedImages, setRemovedImages] = useState([]);
  const [stepChanging, setStepChanging] = useState(false);
  const [removedLogo, setRemovedLogo] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Custom field states — matching AddBusiness exactly
  const [customIndustry, setCustomIndustry] = useState("");
  const [showCustomIndustry, setShowCustomIndustry] = useState(false);
  const [customBusinessType, setCustomBusinessType] = useState("");
  const [showCustomBusinessType, setShowCustomBusinessType] = useState(false);

  // Image error states — matching AddBusiness
  const [logoError, setLogoError] = useState(false);
  const [imagesError, setImagesError] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Match AddBusiness getStepStyle exactly
  const getStepStyle = (step) => ({
    display: current === step ? "block" : "none",
    width: "100%",
  });

  // Match AddBusiness steps exactly
  const steps = [
    "Company",
    "Business",
    "Location",
    "Contact",
    "Social",
    "Images",
  ];

  // Match AddBusiness stepFields exactly — locality added to Location step
  const stepFields = [
    ["companyName", "tagline", "description", "companyUrl", "website", "foundedYear"],
    ["industry", "businessType", "companySize"],
    ["address", "locality", "city", "state", "country", "pincode", "latitude", "longitude"],
    ["contactName", "phone", "email"],
    ["linkedin", "instagram", "twitter"],
    ["logo", "images"],
  ];

  // Match AddBusiness next logic exactly
  const next = async () => {
    if (current === 1) {
      if (showCustomIndustry && (!customIndustry || customIndustry.trim() === "")) {
        message.error("Please enter custom industry");
        return;
      }
      if (showCustomBusinessType && (!customBusinessType || customBusinessType.trim() === "")) {
        message.error("Please enter custom business type");
        return;
      }
    }

    if (current === 5) {
      const logoValue = form.getFieldValue("logo");
      const imagesValue = form.getFieldValue("images");

      if (!logoValue || logoValue.length === 0) {
        setLogoError(true);
        message.error("Please upload a business logo");
        return;
      }

      if (!imagesValue || imagesValue.length === 0) {
        setImagesError(true);
        message.error("Please upload at least one image");
        return;
      }

      setLogoError(false);
      setImagesError(false);
    }

    try {
      await form.validateFields(stepFields[current]);
      setCurrent(current + 1);
      window.scrollTo(0, 0);
    } catch (err) {
      const firstError =
        err.errorFields?.[0]?.errors?.[0] || "Please fill all required fields";
      message.error({
        content: firstError,
        key: "validation-error",
        duration: 2.5,
      });
    }
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setStepChanging(true);
      setCurrent(stepIndex);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setStepChanging(false);
      }, 100);
    }
  }, [steps.length]);

  // Match AddBusiness URL validator exactly
  const validateUrlOptional = async (_, value) => {
    if (!value) return Promise.resolve();
    const normalized = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    try {
      new URL(normalized);
      return Promise.resolve();
    } catch {
      return Promise.reject("Enter a valid URL");
    }
  };

  const normalizeUrl = (url) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  };

  const sanitizeInput = (str) => {
    if (!str) return str;
    return str.replace(/[<>]/g, (char) => ({ "<": "&lt;", ">": "&gt;" }[char]));
  };

  const createUploadFile = (file, index = 0) => {
    if (!file) return null;
    if (typeof file === "string") {
      return {
        uid: `existing-${index}-${Date.now()}`,
        name: `image-${index + 1}`,
        status: "done",
        url: file,
      };
    }
    return {
      uid: file._id || file.uid || `existing-${index}-${Date.now()}`,
      name: file.name || `image-${index + 1}`,
      status: "done",
      url: file.url || file.path || file.location || file.secure_url,
    };
  };

  const fetchBusiness = async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      setPageLoading(true);
      setPageError(false);
      const res = await API.get(`/businesses/${id}`, {
        signal: abortControllerRef.current.signal,
      });
      const item = res.data?.data || res.data;

      const logoFileList = item?.logo
        ? [createUploadFile(item.logo, 0)].filter(Boolean)
        : [];

      const imagesFileList = Array.isArray(item?.images)
        ? item.images.map((img, index) => createUploadFile(img, index)).filter(Boolean)
        : [];

      // Detect if stored industry/type is custom (not in options list)
      const storedIndustry = item?.industry || undefined;
      const isCustomIndustry = storedIndustry && !INDUSTRY_OPTIONS.includes(storedIndustry);
      const storedType = item?.businessType || item?.type || undefined;
      const isCustomType = storedType && !BUSINESS_TYPE_OPTIONS.includes(storedType);

      if (isCustomIndustry) {
        setShowCustomIndustry(true);
        setCustomIndustry(storedIndustry);
      }
      if (isCustomType) {
        setShowCustomBusinessType(true);
        setCustomBusinessType(storedType);
      }

      form.setFieldsValue({
        companyName: item?.companyName || item?.businessName || "",
        tagline: item?.tagline || "",
        description: item?.description || "",
        companyUrl: item?.companyUrl || item?.businessUrl || "",
        website: item?.website || "",
        foundedYear: item?.foundedYear ? String(item.foundedYear) : "",
        industry: isCustomIndustry ? "other" : (storedIndustry || undefined),
        customIndustry: isCustomIndustry ? storedIndustry : "",
        businessType: isCustomType ? "other" : (storedType || undefined),
        customBusinessType: isCustomType ? storedType : "",
        companySize: item?.companySize || undefined,
        address: item?.location?.address || "",
        locality: item?.location?.locality || "",
        city: item?.location?.city || "",
        state: item?.location?.state || "",
        country: item?.location?.country || "",
        pincode: item?.location?.pincode || "",
        latitude: item?.location?.coordinates?.coordinates?.[1] ?? undefined,
        longitude: item?.location?.coordinates?.coordinates?.[0] ?? undefined,
        contactName: item?.contactDetails?.contactName || "",
        phone: item?.contactDetails?.phone || "",
        email: item?.contactDetails?.email || "",
        linkedin: item?.socialLinks?.linkedin || "",
        instagram: item?.socialLinks?.instagram || "",
        twitter: item?.socialLinks?.twitter || "",
        logo: logoFileList,
        images: imagesFileList,
      });

      setIsFormDirty(false);
    } catch (err) {
      if (err.name !== "AbortError" && err.name !== "CanceledError") {
        setPageError(true);
        message.error({
          content: err.response?.data?.message || "Failed to load business details",
        });
      }
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchBusiness();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [id]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedDropdown = e.target.closest(
        ".eb-custom-select"
      );

      if (!clickedDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty]);


  const handleBack = () => {
    if (isFormDirty) {
      setShowUnsavedModal(true);
    } else {
      navigate("/my-businesses");
    }
  };

  const handleKeyDown = (e, callback) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback();
    }
  };

  const onFinish = async (values) => {
    // Final image validation (same as AddBusiness submit button handler)
    const logoValue = form.getFieldValue("logo");
    const imagesValue = form.getFieldValue("images");

    if (!logoValue || logoValue.length === 0) {
      setLogoError(true);
      message.error("Please upload a business logo");
      return;
    }

    if (!imagesValue || imagesValue.length === 0) {
      setImagesError(true);
      message.error("Please upload at least one image");
      return;
    }

    try {
      setLoading(true);

      // Normalize URLs — same as AddBusiness
      let normalizedWebsite = values.website || "";
      if (normalizedWebsite && !/^https?:\/\//i.test(normalizedWebsite)) {
        normalizedWebsite = `https://${normalizedWebsite}`;
      }
      let normalizedCompanyUrl = values.companyUrl || "";
      if (normalizedCompanyUrl && !/^https?:\/\//i.test(normalizedCompanyUrl)) {
        normalizedCompanyUrl = `https://${normalizedCompanyUrl}`;
      }

      const data = {
        companyName: sanitizeInput(values.companyName?.trim()),
        businessName: sanitizeInput(values.companyName?.trim()),
        tagline: sanitizeInput(values.tagline?.trim() || ""),
        description: sanitizeInput(values.description?.trim()),
        companyUrl: normalizedCompanyUrl,
        businessUrl: normalizedCompanyUrl,
        website: normalizedWebsite,
        foundedYear: values.foundedYear ? Number(values.foundedYear) : null,
        industry: values.industry,
        type: values.businessType,
        businessType: values.businessType,
        companySize: values.companySize,
        location: {
          address: sanitizeInput(values.address),
          locality: values.locality ? sanitizeInput(values.locality.trim().toLowerCase()) : "",
          city: sanitizeInput(values.city),
          state: sanitizeInput(values.state),
          country: sanitizeInput(values.country),
          pincode: values.pincode,
          coordinates: {
            type: "Point",
            coordinates: [Number(values.longitude), Number(values.latitude)],
          },
        },
        socialLinks: {
          linkedin: normalizeUrl(values.linkedin?.trim() || ""),
          instagram: normalizeUrl(values.instagram?.trim() || ""),
          twitter: normalizeUrl(values.twitter?.trim() || ""),
        },
        contactDetails: {
          contactName: sanitizeInput(values.contactName),
          phone: values.phone,
          email: values.email,
        },
        // Track existing images for backend merge
        existingLogo:
          !removedLogo && values.logo?.[0] && !values.logo[0].originFileObj
            ? values.logo[0].url
            : null,
        deleteLogo: removedLogo,
        existingImages:
          values.images
            ?.filter((file) => !file.originFileObj)
            .map((file) => file.url)
            .filter(Boolean) || [],
        deletedImages: removedImages,
      };

      // Upload new files to Cloudinary (same as AddBusiness)
      const uploadedLogo = values.logo?.[0]?.originFileObj
        ? await uploadToCloudinary(values.logo[0].originFileObj, "businesses")
        : null;

      const uploadedImages = values.images?.some((f) => f.originFileObj)
        ? await uploadMultipleToCloudinary(
          values.images.map((f) => f.originFileObj).filter(Boolean),
          "businesses"
        )
        : [];

      await API.put(`/businesses/${id}`, {
        ...data,

        // ✅ send only logo URL string
        newLogo: uploadedLogo?.url || "",

        // ✅ send only image URL strings
        newImages: uploadedImages.map(img => img.url),
      });

      message.success("Business updated successfully");
      setIsFormDirty(false);
      setRemovedImages([]);
      setRemovedLogo(false);
      setLogoError(false);
      setImagesError(false);

      setTimeout(() => {
        navigate("/my-businesses");
      }, 500);
    } catch (err) {
      message.error(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const isMobile = windowWidth <= 768;

  if (!pageLoading && pageError) {
    return (
      <div className="eb-page">
        <div className="eb-container">
          <Card className="eb-card">
            <div className="eb-error-state">
              <ExclamationCircleOutlined className="eb-error-icon" />
              <h2>Failed to load business details</h2>
              <p>Something went wrong while fetching the data. Please try again.</p>
              <Button type="primary" className="eb-btn-next" onClick={fetchBusiness}>
                Retry
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="eb-page">
      <div className="eb-container">
        <Spin spinning={pageLoading || stepChanging} description={stepChanging ? "Loading step..." : "Loading business details..."}>
          <Modal
            title={
              <Space>
                <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                <span>Unsaved Changes</span>
              </Space>
            }
            open={showUnsavedModal}
            onOk={() => {
              setShowUnsavedModal(false);
              navigate("/my-businesses");
            }}
            onCancel={() => setShowUnsavedModal(false)}
            okText="Leave"
            cancelText="Stay and Continue Editing"
            okButtonProps={{ danger: true }}
            className="eb-unsaved-modal"
            width={isMobile ? "90%" : 520}
          >
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <Text type="secondary">Your changes will be lost if you don't save them.</Text>
          </Modal>

          <Card className="eb-card" variant={false}>
            {/* Card Header — matches AddBusiness layout */}
            <div className="eb-card-header">
              <div className="eb-card-header-left">
                <Tag className="eb-edit-badge">Editing Mode</Tag>
                <h1 className="eb-title">Edit Business</h1>
                <p className="eb-subtitle">Update your business listing details</p>
              </div>
              <Button
                icon={<CloseOutlined />}
                className="eb-back-btn"
                onClick={handleBack}
                aria-label="Go back"
              >
                {!isMobile && "Back to Listing"}
              </Button>
            </div>

            {/* Stepper — matches AddBusiness stepper exactly */}
            <div className="eb-stepper" role="list">
              {steps.map((step, i) => (
                <div
                  key={step}
                  className={`eb-step ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}
                  onClick={() => goToStep(i)}
                  onKeyDown={(e) => handleKeyDown(e, () => goToStep(i))}
                  role="listitem"
                  aria-label={`Step ${i + 1}: ${step}${current > i ? " (completed)" : current === i ? " (current)" : ""}`}
                  tabIndex={0}
                >
                  <div className="eb-step-line-left" />
                  <div className="eb-step-inner">
                    <div className="eb-circle">
                      {current > i ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className="eb-step-label">{step}</span>
                  </div>
                  <div className="eb-step-line-right" />
                </div>
              ))}
            </div>

            {/* Progress Bar */}
            <div className="eb-progress-bar">
              <div
                className="eb-progress-fill"
                style={{ width: `${(current / (steps.length - 1)) * 100}%` }}
              />
            </div>
            <div className="eb-progress-label">
              Step {current + 1} of {steps.length} — {steps[current]}
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={() => setIsFormDirty(true)}
              validateTrigger="onBlur"
              scrollToFirstError
              className="eb-form"
            >
              {/* Step 0: Company — matches AddBusiness Step 0 exactly */}
              <div style={getStepStyle(0)}>
                <Form.Item
                  name="companyName"
                  label="Company Name"
                  rules={[
                    { required: true, message: "Please enter your company name" },
                    { min: 3, message: "Company name must be at least 3 characters" },
                    { max: 100, message: "Company name cannot exceed 100 characters" },
                    {
                      pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.&'-]*$/,
                      message: "Company name must start with a letter and can contain numbers, spaces, . & ' -",
                    },
                  ]}
                  help={null}
                  validateStatus="success"
                >
                  <Input placeholder="e.g. Acme Corporation" />
                </Form.Item>

                <Form.Item
                  name="tagline"
                  label="Tagline"
                  rules={[
                    {
                      pattern: /^(?=.*[A-Za-z])[A-Za-z0-9\s.,&'-]+$/,
                      message: "Tagline can contain letters, numbers, spaces, and basic punctuation",
                    },
                  ]}
                  help={null}
                  validateStatus="success"
                >
                  <Input placeholder="A short line that describes your business" />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    { required: true, message: "Please provide a description" },
                    { min: 20, message: "Description should be at least 20 characters" },
                  ]}
                  help={null}
                  validateStatus="success"
                >
                  <TextArea rows={5} placeholder="Describe your business, services, products, and what makes you unique..." />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="companyUrl"
                      label="Company URL"
                      rules={[{ validator: validateUrlOptional }]}
                      help={null}
                      validateStatus="success"
                    >
                      <Input placeholder="https://company.com" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="website"
                      label="Website"
                      rules={[{ validator: validateUrlOptional }]}
                      help={null}
                      validateStatus="success"
                    >
                      <Input placeholder="https://website.com" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="foundedYear"
                  label="Founded Year"
                  rules={[
                    { required: true, message: "Please enter the founded year" },
                    {
                      pattern: /^\d{4}$/,
                      message: "Enter a valid 4-digit year",
                    },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const year = Number(value);
                        const currentYear = new Date().getFullYear();
                        if (year < 1800 || year > currentYear) {
                          return Promise.reject(`Year must be between 1800 and ${currentYear}`);
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  help={null}
                  validateStatus="success"
                >
                  <Input maxLength={4} placeholder="e.g. 2015" />
                </Form.Item>
              </div>

              {/* Step 1: Business — matches AddBusiness Step 1 exactly */}
              <div style={getStepStyle(1)}>
                <Form.Item
                  name="industry"
                  label="Industry Type"
                  rules={[{ required: true, message: "Please select an industry" }]}
                >
                  <div
                    className={`eb-custom-select ${openDropdown === "industry"
                      ? "active"
                      : ""
                      }`}
                  >
                    <div
                      className="eb-custom-select-trigger"
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === "industry"
                            ? null
                            : "industry"
                        )
                      }
                    >
                      {form.getFieldValue("industry")
                        ? form
                          .getFieldValue("industry")
                          .split("_")
                          .map(
                            (w) =>
                              w.charAt(0).toUpperCase() +
                              w.slice(1)
                          )
                          .join(" ")
                        : "Select industry"}

                      <span className="eb-custom-arrow">
                        ⌄
                      </span>
                    </div>

                    {openDropdown === "industry" && (
                      <div className="eb-custom-dropdown">
                        {INDUSTRY_OPTIONS.map((item) => (
                          <div
                            key={item}
                            className="eb-custom-dropdown-item"
                            onClick={() => {
                              form.setFieldValue(
                                "industry",
                                item
                              );

                              if (item === "other") {
                                setShowCustomIndustry(true);
                              } else {
                                setShowCustomIndustry(false);
                                setCustomIndustry("");
                              }

                              setOpenDropdown(null);
                            }}
                          >
                            {item
                              .split("_")
                              .map(
                                (w) =>
                                  w.charAt(0).toUpperCase() +
                                  w.slice(1)
                              )
                              .join(" ")}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Form.Item>
                {showCustomIndustry && (
                  <Form.Item
                    name="customIndustry"
                    label="Enter Custom Industry"
                    rules={[
                      { required: true, message: "Please enter custom industry" },
                      { min: 2, message: "Industry must be at least 2 characters" },
                      { pattern: /^[A-Za-z][A-Za-z\s&-]*$/, message: "Start with letter, can include letters, spaces, & and -" },
                    ]}
                    style={{ marginTop: -8 }}
                  >
                    <Input
                      placeholder="e.g. Manufacturing, Hospitality, Banking"
                      value={customIndustry}
                      onChange={(e) => {
                        setCustomIndustry(e.target.value);
                        form.setFieldValue("industry", e.target.value);
                      }}
                    />
                  </Form.Item>
                )}

                <Form.Item
                  name="businessType"
                  label="Business Type"
                  rules={[{ required: true, message: "Please select a business type" }]}
                >
                  <div
                    className={`eb-custom-select ${openDropdown === "businessType"
                      ? "active"
                      : ""
                      }`}
                  >
                    <div
                      className="eb-custom-select-trigger"
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === "businessType"
                            ? null
                            : "businessType"
                        )
                      }
                    >
                      {form.getFieldValue("businessType")
                        ? form
                          .getFieldValue("businessType")
                          .charAt(0)
                          .toUpperCase() +
                        form
                          .getFieldValue("businessType")
                          .slice(1)
                        : "Select business type"}

                      <span className="eb-custom-arrow">
                        ⌄
                      </span>
                    </div>

                    {openDropdown ===
                      "businessType" && (
                        <div className="eb-custom-dropdown">
                          {BUSINESS_TYPE_OPTIONS.map(
                            (item) => (
                              <div
                                key={item}
                                className="eb-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "businessType",
                                    item
                                  );

                                  if (item === "other") {
                                    setShowCustomBusinessType(
                                      true
                                    );
                                  } else {
                                    setShowCustomBusinessType(
                                      false
                                    );
                                    setCustomBusinessType("");
                                  }

                                  setOpenDropdown(null);
                                }}
                              >
                                {item.charAt(0).toUpperCase() +
                                  item.slice(1)}
                              </div>
                            )
                          )}
                        </div>
                      )}
                  </div>
                </Form.Item>
                {showCustomBusinessType && (
                  <Form.Item
                    name="customBusinessType"
                    label="Enter Custom Business Type"
                    rules={[
                      { required: true, message: "Please enter custom business type" },
                      { min: 2, message: "Business type must be at least 2 characters" },
                      { pattern: /^[A-Za-z][A-Za-z\s&-]*$/, message: "Start with letter, can include letters, spaces, & and -" },
                    ]}
                    style={{ marginTop: -8 }}
                  >
                    <Input
                      placeholder="e.g. LLP, Trust, Society"
                      value={customBusinessType}
                      onChange={(e) => {
                        setCustomBusinessType(e.target.value);
                        form.setFieldValue("businessType", e.target.value);
                      }}
                    />
                  </Form.Item>
                )}

                <Form.Item
                  name="companySize"
                  label="Company Size"
                  rules={[{ required: true, message: "Please select a company size" }]}
                >
                 <div
  className={`eb-custom-select eb-drop-up ${
    openDropdown === "companySize"
      ? "active"
      : ""
  }`}
>
                    <div
                      className="eb-custom-select-trigger"
                      onClick={() =>
                        setOpenDropdown(
                          openDropdown === "companySize"
                            ? null
                            : "companySize"
                        )
                      }
                    >
                      {form.getFieldValue("companySize")
                        ? `${form.getFieldValue(
                          "companySize"
                        )} employees`
                        : "Select company size"}

                      <span className="eb-custom-arrow">
                        ⌄
                      </span>
                    </div>

                    {openDropdown ===
                      "companySize" && (
                        <div className="eb-custom-dropdown">
                          {COMPANY_SIZE_OPTIONS.map(
                            (item) => (
                              <div
                                key={item}
                                className="eb-custom-dropdown-item"
                                onClick={() => {
                                  form.setFieldValue(
                                    "companySize",
                                    item
                                  );

                                  setOpenDropdown(null);
                                }}
                              >
                                {item} employees
                              </div>
                            )
                          )}
                        </div>
                      )}
                  </div>
                </Form.Item>
              </div>

              {/* Step 2: Location — locality field added, same as CommercialSpace */}
              <div style={getStepStyle(2)}>
                <Form.Item
                  name="address"
                  label="Address"
                  rules={[
                    { required: true, message: "Please enter the Address" },
                    {
                      pattern: /^[A-Za-z0-9\s,./#()&-]+$/,
                      message: "Please enter a valid address",
                    },
                  ]}
                  help={null}
                  validateStatus="success"
                >
                  <Input placeholder="e.g. #12, MG Road, Bengaluru" />
                </Form.Item>

                {/* LOCALITY FIELD — using LocationField for suggestions, same as CommercialSpace */}
                <Form.Item
                  name="locality"
                  label="Locality"
                  rules={[
                    { required: true, message: "Please enter locality" },
                    { min: 2, message: "Locality must be at least 2 characters" },
                    { max: 100, message: "Locality cannot exceed 100 characters" },
                    {
                      pattern: /^(?=.*[A-Za-z])[A-Za-z0-9\s.,#&()-]+$/,
                      message: "Locality must contain at least one letter and can include numbers and symbols like . , # & ( ) -",
                    },
                  ]}
                  help={null}
                  validateStatus="success"
                >
                  <LocationField
                    form={form}
                    message={message}
                    setAutoDetecting={setAutoDetecting}
                  />
                </Form.Item>

                <Form.Item
                  name="city"
                  label="City"
                  rules={[
                    { required: true, message: "Please enter the city" },
                    { pattern: /^[A-Za-z\s.-]+$/, message: "City name should contain only letters" },
                  ]}
                  help={null}
                  validateStatus="success"
                >
                  <Input placeholder="e.g. Bengaluru" />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="state"
                      label="State"
                      rules={[
                        { required: true, message: "Please enter the State" },
                        { pattern: /^[A-Za-z\s.-]+$/, message: "State name should contain only letters" },
                      ]}
                      help={null}
                      validateStatus="success"
                    >
                      <Input placeholder="e.g. Karnataka" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="country"
                      label="Country"
                      rules={[
                        { required: true, message: "Please enter the country" },
                        { pattern: /^[A-Za-z\s.-]+$/, message: "Country name should contain only letters" },
                      ]}
                      help={null}
                      validateStatus="success"
                    >
                      <Input placeholder="e.g. India" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="pincode"
                  label="Pincode"
                  rules={[
                    { required: true, message: "Pincode is required" },
                    { pattern: /^[1-9][0-9]{5}$/, message: "Enter a valid 6-digit pincode" },
                  ]}
                >
                  <Input maxLength={6} placeholder="e.g. 560038" style={{ maxWidth: 200 }} />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="latitude"
                      label="Latitude"
                      rules={[
                        { required: true, message: "Latitude is required" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "")
                              return Promise.reject("Latitude is required");
                            const str = value.toString();
                            if ((str.match(/\./g) || []).length > 1)
                              return Promise.reject("Only one decimal point allowed");
                            const parts = str.split(".");
                            if (parts[1] && parts[1].length > 6)
                              return Promise.reject("Max 6 digits allowed after decimal");
                            if (typeof value !== "number" || Number.isNaN(value))
                              return Promise.reject("Enter a valid latitude");
                            if (value < -90 || value > 90)
                              return Promise.reject("Latitude must be between -90 and 90");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-90}
                        max={90}
                        step={0.000001}
                        placeholder="e.g. 12.971891"
                        onBlur={() => {
                          const val = form.getFieldValue("latitude");
                          if (typeof val === "number") {
                            form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="longitude"
                      label="Longitude"
                      rules={[
                        { required: true, message: "Longitude is required" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "")
                              return Promise.reject("Longitude is required");
                            const str = value.toString();
                            if ((str.match(/\./g) || []).length > 1)
                              return Promise.reject("Only one decimal point allowed");
                            const parts = str.split(".");
                            if (parts[1] && parts[1].length > 6)
                              return Promise.reject("Max 6 digits allowed after decimal");
                            if (typeof value !== "number" || Number.isNaN(value))
                              return Promise.reject("Enter a valid longitude");
                            if (value < -180 || value > 180)
                              return Promise.reject("Longitude must be between -180 and 180");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={-180}
                        max={180}
                        step={0.000001}
                        placeholder="e.g. 77.594563"
                        onBlur={() => {
                          const val = form.getFieldValue("longitude");
                          if (typeof val === "number") {
                            form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Step 3: Contact — matches AddBusiness Step 3 exactly */}
              <div style={getStepStyle(3)}>
                <Form.Item
                  name="contactName"
                  label="Contact Person Name"
                  rules={[
                    { required: true, message: "Please enter contact name" },
                    { pattern: /^[A-Za-z\s.-]+$/, message: "Name can only contain letters and basic characters" },
                  ]}
                  help={null}
                  validateStatus="success"
                >
                  <Input placeholder="e.g. Rajesh Kumar" />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[
                        { required: true, message: "Please enter a phone number" },
                        { pattern: /^[6-9]\d{9}$/, message: "Enter a valid 10-digit Indian phone number" },
                      ]}
                      help={null}
                      validateStatus="success"
                    >
                      <Input maxLength={10} placeholder="e.g. 9876543210" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[
                        { required: true, message: "Please enter your email address" },
                        { type: "email", message: "Enter a valid email address" },
                      ]}
                      help={null}
                      validateStatus="success"
                    >
                      <Input placeholder="e.g. contact@company.com" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Step 4: Social — matches AddBusiness Step 4 exactly */}
              <div style={getStepStyle(4)}>
                <Form.Item
                  name="linkedin"
                  label="LinkedIn"
                  rules={[{ validator: validateUrlOptional }]}
                  help={null}
                  validateStatus="success"
                >
                  <Input placeholder="https://linkedin.com/company/your-brand" />
                </Form.Item>

                <Form.Item
                  name="instagram"
                  label="Instagram"
                  rules={[{ validator: validateUrlOptional }]}
                  help={null}
                  validateStatus="success"
                >
                  <Input placeholder="https://instagram.com/yourbrand" />
                </Form.Item>

                <Form.Item
                  name="twitter"
                  label="Twitter / X"
                  rules={[{ validator: validateUrlOptional }]}
                  help={null}
                  validateStatus="success"
                >
                  <Input placeholder="https://twitter.com/yourbrand" />
                </Form.Item>
              </div>

              {/* Step 5: Images — matches AddBusiness Step 5 exactly */}
              <div style={getStepStyle(5)}>
                <Form.Item
                  name="logo"
                  label={
                    <span className="eb-label-with-badge">
                      Business Logo
                      <span className="eb-badge-primary">Required</span>
                    </span>
                  }
                  valuePropName="fileList"
                  getValueFromEvent={(e) => e?.fileList}
                  rules={[{ required: true, message: "Please upload a business logo" }]}
                  validateStatus={logoError ? "error" : ""}
                  help={logoError ? "Please upload a business logo" : ""}
                >
                  <Upload.Dragger
                    maxCount={1}
                    accept="image/png,image/jpeg"
                    className="eb-upload"
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(file.type);
                      if (!isValidType) {
                        message.error("Only PNG, JPEG files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    onChange={() => {
                      setLogoError(false);
                      setIsFormDirty(true);
                    }}
                    onRemove={() => {
                      setRemovedLogo(true);
                      setIsFormDirty(true);
                    }}
                  >
                    <div className="eb-upload-inner">
                      <p className="eb-upload-title">Upload Business Logo</p>
                      <p className="eb-upload-hint">Square image recommended · PNG, JPEG</p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>

                <Form.Item
                  name="images"
                  valuePropName="fileList"
                  label={
                    <span className="eb-label-with-badge">
                      Business Images
                      <span className="eb-badge-secondary">Up to 5</span>
                    </span>
                  }
                  getValueFromEvent={(e) => e.fileList}
                  rules={[
                    { required: true, message: "Please upload at least one business image" },
                    {
                      validator: (_, value) => {
                        if (!value || value.length === 0)
                          return Promise.reject("Please upload at least one business image");
                        if (value.length > 5)
                          return Promise.reject("Maximum 5 images allowed");
                        return Promise.resolve();
                      },
                    },
                  ]}
                  validateStatus={imagesError ? "error" : ""}
                  help={imagesError ? "Please upload at least one business image" : ""}
                >
                  <Upload.Dragger
                    beforeUpload={(file) => {
                      const isValidType = ["image/png", "image/jpeg"].includes(file.type);
                      if (!isValidType) {
                        message.error("Only PNG, JPEG files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      return false;
                    }}
                    maxCount={5}
                    accept="image/png,image/jpeg"
                    multiple
                    className="eb-upload"
                    onChange={() => {
                      setImagesError(false);
                      setIsFormDirty(true);
                    }}
                    onRemove={(file) => {
                      if (!file.originFileObj && file.url) {
                        setRemovedImages((prev) => [...prev, file.url]);
                      }
                    }}
                  >
                    <div className="eb-upload-inner">
                      <p className="eb-upload-title">Upload Business Images</p>
                      <p className="eb-upload-hint">Add up to 5 images · PNG, JPEG</p>
                    </div>
                  </Upload.Dragger>
                </Form.Item>
              </div>

              {/* Navigation Buttons — matches AddBusiness */}
              <div className="eb-btns">
                {current > 0 && (
                  <Button
                    onClick={prev}
                    className="eb-btn-prev"
                    icon={<ArrowLeftOutlined />}
                    disabled={autoDetecting}
                  >
                    Previous
                  </Button>
                )}
                <div className="eb-btns-right">
                  {current < steps.length - 1 && (
                    <Button
                      type="primary"
                      onClick={next}
                      className="eb-btn-next"
                      disabled={autoDetecting}
                    >
                      Next →
                    </Button>
                  )}
                  {current === steps.length - 1 && (
                    <Button
                      type="primary"
                      onClick={async () => {
                        const logoValue = form.getFieldValue("logo");
                        const imagesValue = form.getFieldValue("images");

                        if (!logoValue || logoValue.length === 0) {
                          setLogoError(true);
                          message.error("Please upload a business logo");
                          return;
                        }

                        if (!imagesValue || imagesValue.length === 0) {
                          setImagesError(true);
                          message.error("Please upload at least one image");
                          return;
                        }

                        setLogoError(false);
                        setImagesError(false);
                        form.submit();
                      }}
                      loading={loading}
                      disabled={loading}
                      icon={<SaveOutlined />}
                      className="eb-btn-submit"
                    >
                      {loading ? "Updating..." : "Update Business"}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </Card>
        </Spin>
      </div>
    </div>
  );
}

export default EditBusiness;